/**
 * Which pages a role is SHOWN — the sidebar, the Settings tab strip and the route guards
 * all read these, so a hidden link and a blocked URL never disagree.
 *
 * Display and navigation only: the API re-checks every call. Product decision 2026-10-20:
 * a Solution Architect does not see the Resources page, nor a System item it cannot act
 * on. Items with nothing to change (Logs, Settings → Signals) stay visible to every role.
 * See `viewResourcesPage` / `viewSystemPagesReadOnly` in src/lib/permissions.ts.
 */

import { type Capability, hasCapability } from "@/lib/permissions";

type Role = string | null | undefined;

export interface SettingsTab {
  label: string;
  href: string;
  /**
   * The capability a tab's changes need — a role without it sees the tab view-only.
   * `null` means the tab has nothing to change.
   */
  write: Capability | null;
  /** The tab's CONTENT (not just its controls) is withheld from roles without this. */
  protectedFor?: { capability: Capability; notice: string };
}

export const SETTINGS_TABS: readonly SettingsTab[] = [
  { label: "Users", href: "/dashboard/settings/users", write: "manageUsers" },
  { label: "Disciplines", href: "/dashboard/settings/disciplines", write: "manageConfiguration" },
  { label: "Shapes", href: "/dashboard/settings/pod-composition", write: "manageConfiguration" },
  // Tiers sits beside Shapes: a tier's default Shape and its rate card are configured
  // together, and both are read as "what does this tier commit to".
  { label: "Tiers", href: "/dashboard/settings/tiers", write: "manageConfiguration" },
  { label: "Constraints", href: "/dashboard/settings/constraints", write: "manageConfiguration" },
  { label: "Intensity", href: "/dashboard/settings/intensity", write: "manageConfiguration" },
  {
    label: "Request Priorities",
    href: "/dashboard/settings/request-priorities",
    write: "manageConfiguration",
  },
  // Resource Plan M1 — one tab per epic (11, 12, 14).
  {
    label: "Labor Categories",
    href: "/dashboard/settings/labor-categories",
    write: "manageConfiguration",
  },
  { label: "Rate Card", href: "/dashboard/settings/rate-card", write: "manageRateCard" },
  { label: "Planning", href: "/dashboard/settings/planning", write: "manageRateCard" },
  // Resource-level cost (2026-10-14): ADMIN / FIN / RM (RM read-only), hidden from everyone
  // else like Users, and the page itself asks the user to re-authenticate on every visit.
  {
    label: "Internal Rates",
    href: "/dashboard/settings/internal-rates",
    write: "manageResourceInternalRates",
    protectedFor: {
      capability: "viewResourceInternalRates",
      notice:
        "Internal Rates are each Resource's own cost, so only Admin, Finance and Resource Managers can see them, and only Admin and Finance can change them.",
    },
  },
  // Resource Plan M3 — Epic 13.
  {
    label: "Workstream Templates",
    href: "/dashboard/settings/workstream-templates",
    write: "manageConfiguration",
  },
  { label: "System", href: "/dashboard/settings/system", write: "manageConfiguration" },
  {
    label: "Rundown",
    href: "/dashboard/settings/rundown",
    write: "configureRundownSettings",
    // The thresholds decide which teammates appear on a leadership utilization slide, so
    // the page opens but its content is not public (see the /api/rundown/config comment).
    protectedFor: {
      capability: "configureRundownSettings",
      notice:
        "Rundown thresholds decide which teammates appear on the leadership utilization slide, so only the Resource Manager can see and change them.",
    },
  },
  { label: "Signals", href: "/dashboard/settings/signals", write: null },
];

/** Integrations' changes: Refresh rides on manageConfiguration, Import on runImportFromNymbl. */
const INTEGRATIONS_WRITE = ["manageConfiguration", "runImportFromNymbl"] as const;

/**
 * Is a System item (page or Settings tab) shown to `role`? Yes when it has nothing to
 * change, when the role can make its changes, or when the role may see it view-only.
 */
export function canSeeSystemItem(
  role: Role,
  write: Capability | readonly Capability[] | null,
): boolean {
  if (write === null) return true;
  const writes: readonly Capability[] = typeof write === "string" ? [write] : write;
  if (writes.some((capability) => hasCapability(role, capability))) return true;
  return hasCapability(role, "viewSystemPagesReadOnly");
}

/** The Settings tabs `role` is shown, in strip order. */
export function visibleSettingsTabs(role: Role): SettingsTab[] {
  return SETTINGS_TABS.filter(
    (tab) =>
      // Users is user administration (roles, passwords, sessions) — Admin/RM only.
      (tab.write !== "manageUsers" || hasCapability(role, "manageUsers")) &&
      // Internal Rates (Resource-level cost, 2026-10-14) is hidden the same way — and its
      // protectedFor notice still covers anyone who reaches the URL directly.
      (tab.write !== "manageResourceInternalRates" ||
        hasCapability(role, "viewResourceInternalRates")) &&
      canSeeSystemItem(role, tab.write),
  );
}

/** Is the Settings tab at `href` shown to `role`? For in-page links into Settings. */
export function canSeeSettingsTab(role: Role, href: string): boolean {
  return visibleSettingsTabs(role).some((tab) => tab.href === href);
}

export function canSeeIntegrations(role: Role): boolean {
  return canSeeSystemItem(role, INTEGRATIONS_WRITE);
}

export function canSeeResourcesPage(role: Role): boolean {
  return hasCapability(role, "viewResourcesPage");
}
